import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAdminAuth } from '../hooks/useAdminAuth';
import InvoicesList from '../components/Admin/InvoicesList';
import FacturesDuSite from '../components/Admin/FacturesDuSite';
import CreateInvoiceForm from '../components/Admin/CreateInvoiceForm';

import ContactList from '../components/Contacts/ContactList';
import DiscountManager from '../components/Admin/DiscountManager';
import StockManager from '../components/Admin/StockManager';
import DashboardHome from './admin/DashboardHome';
import AudienceDashboard from '../components/Admin/AudienceDashboard';
import EmailingPanel from '../components/Admin/EmailingPanel';

export default function Admin() {
  const { isAdmin } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  if (!isAdmin) {
    return (
      <>
        <Helmet>
          <title>Accès Refusé - Mirum Games</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="admin-access-denied">
          <h1>Accès Refusé</h1>
          <p>Vous n'avez pas les permissions pour accéder à cette page.</p>
        </div>
      </>
    );
  }

  const handleInvoiceCreated = () => {
    // Trigger refresh of invoices list
    setRefreshTrigger((prev) => prev + 1);
    // Switch to invoices tab to show the new invoice
    setActiveTab('invoices');
  };

  return (
    <>
      <Helmet>
        <title>Dashboard Admin - Mirum Games</title>
        <meta name="description" content="Dashboard d'administration pour gérer les factures Stripe" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pb-12">
        <div className="bg-white shadow-sm">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Dashboard Admin</h1>
            <p className="mt-2 text-sm text-gray-500">Ventes, clients, factures et stocks — Mirum Games</p>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-8 overflow-x-auto">
            <div className="flex space-x-2 rounded-xl bg-white p-1 shadow-sm">
              {[
                { id: 'dashboard', label: '📊 Dashboard' },
                { id: 'audience', label: '👣 Audience' },
                { id: 'emailing', label: '✉️ Emailing' },
                { id: 'contacts', label: '👥 Contacts (CRM)' },
                { id: 'invoices', label: '📋 Liste des Factures' },
                { id: 'create', label: '➕ Créer une Facture' },
                { id: 'discounts', label: '🏷️ Réductions' },
                { id: 'stocks', label: '📦 Stocks' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200
                    ${activeTab === tab.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="min-h-[500px]">
            {activeTab === 'dashboard' && (
              <div className="animate-fade-in">
                <DashboardHome />
              </div>
            )}

            {activeTab === 'audience' && (
              <div className="animate-fade-in">
                <AudienceDashboard />
              </div>
            )}

            {activeTab === 'emailing' && (
              <div className="animate-fade-in">
                <EmailingPanel />
              </div>
            )}

            {activeTab === 'contacts' && (
              <div className="animate-fade-in">
                <ContactList />
              </div>
            )}

            {activeTab === 'invoices' && (
              <div className="animate-fade-in space-y-10">
                <FacturesDuSite />
                <div>
                  <p className="mb-2 text-sm text-gray-500">
                    Factures Stripe : ventes de Charlotte Ansiaux jusqu’au 22/09/2026 et factures
                    manuelles antérieures.
                  </p>
                  <InvoicesList key={refreshTrigger} />
                </div>
              </div>
            )}

            {activeTab === 'create' && (
              <div className="animate-fade-in">
                <CreateInvoiceForm onInvoiceCreated={handleInvoiceCreated} />
              </div>
            )}

            {activeTab === 'discounts' && (
              <div className="animate-fade-in">
                <DiscountManager />
              </div>
            )}

            {activeTab === 'stocks' && (
              <div className="animate-fade-in">
                <StockManager />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
